import { copyVehicleScheduleMutation, getAllVehiclesOptions } from '@/api/main'
import type { CopyVehicleScheduleFilter, CopyVehicleScheduleRequestBody, VehicleResponse } from '@/api/main/types.gen'
import { invalidateSchedulerQueries } from '@/routes/scheduling.$startDate.$endDate.drive.$action.$id'
import { ActionIcon, Alert, Button, Group, Radio, Select, Stack, Table, Text } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TbMinus, TbPlus } from 'react-icons/tb'
import { ConfirmSummary } from './ConfirmSummary'
import { copyConfirmLines } from '@/utils/autoScheduleConfirm'
import {
  buildCopyVehicleScheduleBodies,
  calendarDateToPickerDate,
  completeMappings,
  copyMappingLabel,
  isIdentityCopyOnSameDay,
  pickerDateToCalendarDate,
  runCopyVehicleScheduleRequests,
  type VehicleScheduleMapping,
} from '@/utils/autoScheduleCopy'
import { canSubmitCopy, defaultCopySnapshot, isCopyDirty, shouldConfirmSubmit } from '@/utils/autoScheduleDirty'
import { getTimezone } from '@/utils/datetime'

const FILTERS: CopyVehicleScheduleFilter[] = ['all', 'only_started', 'not_manually_cancelled']

type MappingRow = VehicleScheduleMapping & { key: string }

let mappingSeq = 0
const nextKey = () => {
  mappingSeq += 1
  return `map-${mappingSeq}`
}

export function CopyMode({
  vehicleId,
  targetWindowStart,
  onDirtyChange,
  onFinished,
}: {
  vehicleId: string
  targetWindowStart: string
  onDirtyChange?: (dirty: boolean) => void
  onFinished: () => void
}) {
  const { t } = useTranslation(['scheduling', 'common'])
  const queryClient = useQueryClient()
  const tz = getTimezone()
  const { data: vehicles } = useSuspenseQuery(getAllVehiclesOptions())
  const [initial] = useState(() => defaultCopySnapshot(vehicleId, targetWindowStart, tz))
  const [sourceDate, setSourceDate] = useState(initial.sourceDate)
  const [destinationDate, setDestinationDate] = useState(initial.destinationDate)
  const [filter, setFilter] = useState<CopyVehicleScheduleFilter>(initial.filter)
  const [rows, setRows] = useState<MappingRow[]>(() =>
    initial.mappings.map(mapping => ({ ...mapping, key: nextKey() }))
  )
  const [failedBodies, setFailedBodies] = useState<CopyVehicleScheduleRequestBody[]>([])

  useEffect(() => {
    onDirtyChange?.(isCopyDirty({ sourceDate, destinationDate, filter, mappings: rows }, initial))
  }, [sourceDate, destinationDate, filter, rows, initial, onDirtyChange])

  useEffect(() => {
    setFailedBodies([])
  }, [sourceDate, destinationDate, filter, rows])

  const vehicleOptions = useMemo(
    () => vehicles.map((vehicle: VehicleResponse) => ({ value: vehicle.id, label: vehicle.id.toUpperCase() })),
    [vehicles]
  )

  const { mutateAsync: copyOne, isPending } = useMutation(copyVehicleScheduleMutation())

  const mappings = completeMappings(rows)
  const hasInvalidIdentity = mappings.some(mapping => isIdentityCopyOnSameDay(mapping, sourceDate, destinationDate))

  const executeCopy = async (bodies: CopyVehicleScheduleRequestBody[]) => {
    const {
      succeeded,
      failed,
      failedBodies: nextFailed,
    } = await runCopyVehicleScheduleRequests(bodies, body => copyOne({ body }))
    if (failed === 0) {
      showNotification({
        message: t('autoSchedule.copySucceeded', { count: succeeded }),
        color: 'green',
      })
      setFailedBodies([])
      invalidateSchedulerQueries(queryClient)
      onFinished()
      return
    }
    setFailedBodies(nextFailed)
    if (succeeded > 0) {
      showNotification({
        message: t('autoSchedule.copyPartial', { succeeded, failed }),
        color: 'yellow',
      })
      invalidateSchedulerQueries(queryClient)
      return
    }
    showNotification({ message: t('autoSchedule.copyFailed'), color: 'red' })
  }

  const confirm = () => {
    if (!shouldConfirmSubmit(canSubmitCopy(mappings, sourceDate, destinationDate))) {
      return
    }
    const lines = copyConfirmLines({
      sourceDate,
      destinationDate,
      filter,
      mappings,
      filterLabels: {
        all: t('autoSchedule.copyFilterAll'),
        only_started: t('autoSchedule.copyFilterStarted'),
        not_manually_cancelled: t('autoSchedule.copyFilterNotCancelled'),
      },
      labels: {
        sourceDate: t('autoSchedule.sourceDate'),
        destinationDate: t('autoSchedule.targetDate'),
        filter: t('autoSchedule.copyFilter'),
        mappings: t('autoSchedule.mappings'),
      },
    })
    modals.openConfirmModal({
      title: t('autoSchedule.confirmCopyTitle'),
      centered: true,
      zIndex: 250,
      children: <ConfirmSummary lines={lines} />,
      labels: { confirm: t('confirm', { ns: 'common' }), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'blue' },
      onConfirm: () => {
        void executeCopy(buildCopyVehicleScheduleBodies(mappings, sourceDate, destinationDate, filter))
      },
    })
  }

  return (
    <Stack gap="lg" maw={820} mx="auto" w="100%" px="md" py="sm" h="100%" style={{ overflow: 'auto' }}>
      <Group grow>
        <DatePickerInput
          label={t('autoSchedule.sourceDate')}
          value={calendarDateToPickerDate(sourceDate)}
          onChange={value => {
            if (value) {
              setSourceDate(pickerDateToCalendarDate(value))
            }
          }}
        />
        <DatePickerInput
          label={t('autoSchedule.targetDate')}
          value={calendarDateToPickerDate(destinationDate)}
          onChange={value => {
            if (value) {
              setDestinationDate(pickerDateToCalendarDate(value))
            }
          }}
        />
      </Group>
      <Radio.Group
        label={t('autoSchedule.copyFilter')}
        value={filter}
        onChange={value => setFilter(value as CopyVehicleScheduleFilter)}
      >
        <Group mt="xs">
          {FILTERS.map(item => (
            <Radio key={item} value={item} label={t(`autoSchedule.copyFilter${filterKey(item)}`)} />
          ))}
        </Group>
      </Radio.Group>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>{t('autoSchedule.vehicleMapping')}</Text>
          <ActionIcon
            variant="light"
            aria-label={t('autoSchedule.addMapping')}
            onClick={() =>
              setRows(current => [...current, { key: nextKey(), sourceVehicleId: '', destinationVehicleId: '' }])
            }
          >
            <TbPlus />
          </ActionIcon>
        </Group>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('autoSchedule.sourceVehicle')}</Table.Th>
              <Table.Th>{t('autoSchedule.targetVehicle')}</Table.Th>
              <Table.Th w={48} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(row => (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <Select
                    searchable
                    data={vehicleOptions}
                    value={row.sourceVehicleId || null}
                    onChange={value =>
                      setRows(current =>
                        current.map(item => (item.key === row.key ? { ...item, sourceVehicleId: value ?? '' } : item))
                      )
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    searchable
                    data={vehicleOptions}
                    value={row.destinationVehicleId || null}
                    onChange={value =>
                      setRows(current =>
                        current.map(item =>
                          item.key === row.key ? { ...item, destinationVehicleId: value ?? '' } : item
                        )
                      )
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    disabled={rows.length === 1}
                    aria-label={t('autoSchedule.removeMapping')}
                    onClick={() => setRows(current => current.filter(item => item.key !== row.key))}
                  >
                    <TbMinus />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {hasInvalidIdentity && (
          <Text size="sm" c="red">
            {t('autoSchedule.identityCopyBlocked')}
          </Text>
        )}
      </Stack>
      {failedBodies.length > 0 ? (
        <Alert color="yellow" title={t('autoSchedule.copyRetryTitle')}>
          <Stack gap="xs">
            <Text size="sm">{t('autoSchedule.copyRetryBody', { count: failedBodies.length })}</Text>
            {failedBodies.map(body => (
              <Text key={copyMappingLabel(body)} size="sm" ff="monospace">
                {copyMappingLabel(body).toUpperCase()}
              </Text>
            ))}
            <Button
              size="xs"
              variant="light"
              loading={isPending}
              onClick={() => void executeCopy(failedBodies)}
              style={{ alignSelf: 'flex-start' }}
            >
              {t('autoSchedule.copyRetry')}
            </Button>
          </Stack>
        </Alert>
      ) : null}
      <Button loading={isPending} disabled={mappings.length === 0 || hasInvalidIdentity} onClick={confirm}>
        {t('confirm', { ns: 'common' })}
      </Button>
    </Stack>
  )
}

const filterKey = (filter: CopyVehicleScheduleFilter): 'All' | 'Started' | 'NotCancelled' => {
  if (filter === 'only_started') {
    return 'Started'
  }
  if (filter === 'not_manually_cancelled') {
    return 'NotCancelled'
  }
  return 'All'
}
